/** Strip optional ```json ... ``` wrapper from pasted text. */
export function stripJsonCodeFence(text) {
  let s = String(text || "").trim();
  if (!s) return s;
  const whole = /^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/i;
  const mWhole = s.match(whole);
  if (mWhole) return mWhole[1].trim();
  const inner = /```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```/i;
  const mInner = s.match(inner);
  if (mInner) return mInner[1].trim();
  return s;
}

const MEETING_TYPES = ["employee_interview", "project_meeting", "other"];

export function normalizeMeetingType(raw) {
  const value = String(raw || "").trim();
  return MEETING_TYPES.includes(value) ? value : undefined;
}

export function ensureEntryHtml(text) {
  const value = String(text || "").trim();
  if (!value) return "";
  if (/<[a-z][\s\S]*>/i.test(value)) return value;
  const escaped = value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<p>${escaped.replace(/\n+/g, "</p><p>")}</p>`;
}

function plainText(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim();
}

export function normalizeMeetingMinutesEntry(raw, order = 0) {
  if (raw == null) return null;
  const description =
    typeof raw === "string" ? raw : raw.description || raw.text || raw.title || "";
  const html = ensureEntryHtml(description);
  if (!plainText(html)) return null;
  const deadline =
    raw && typeof raw === "object" && raw.deadline
      ? String(raw.deadline).trim()
      : "";
  return {
    description: html,
    deadline: /^\d{4}-\d{2}-\d{2}$/.test(deadline) ? deadline : "",
    order,
  };
}

export function normalizeMeetingMinutesGroup(raw, order = 0) {
  if (!raw || typeof raw !== "object") return null;
  const title = String(raw.title || raw.name || "").trim();
  if (!title) return null;
  const entriesSource = Array.isArray(raw.entries)
    ? raw.entries
    : Array.isArray(raw.items)
      ? raw.items
      : [];
  const entries = entriesSource
    .map((entry, index) => normalizeMeetingMinutesEntry(entry, index))
    .filter(Boolean);
  return {
    title,
    description: String(raw.description || "").trim(),
    order,
    entries,
  };
}

/**
 * Parse pasted/generated string into meeting-minutes agenda shape.
 * Accepts: groups array, { groups }, { points }, or full template shape.
 */
export function parseImportedMeetingMinutesJson(text) {
  const trimmed = stripJsonCodeFence(text);
  if (!trimmed) {
    const err = new Error("empty");
    err.code = "empty";
    throw err;
  }

  let data;
  try {
    data = JSON.parse(trimmed);
  } catch {
    const err = new Error("invalid_json");
    err.code = "invalid_json";
    throw err;
  }

  let arr;
  let title;
  let description;
  let meeting_type;

  if (Array.isArray(data)) {
    arr = data;
  } else if (data && Array.isArray(data.groups)) {
    arr = data.groups;
    title = data.title;
    description = data.description;
    meeting_type = data.meeting_type;
  } else if (data && Array.isArray(data.points)) {
    arr = data.points;
    title = data.title;
    description = data.description;
    meeting_type = data.meeting_type;
  } else {
    const err = new Error("no_groups_array");
    err.code = "no_groups_array";
    throw err;
  }

  const groups = arr
    .map((row, index) => normalizeMeetingMinutesGroup(row, index))
    .filter(Boolean);

  if (groups.length === 0) {
    const err = new Error("no_valid_groups");
    err.code = "no_valid_groups";
    throw err;
  }

  return {
    groups,
    title: title != null ? String(title).trim() : undefined,
    description: description != null ? String(description).trim() : undefined,
    meeting_type: normalizeMeetingType(meeting_type),
  };
}

export function toEditorGroups(groups, startOrder = 0) {
  const stamp = Date.now();
  return (groups || []).map((group, index) => ({
    key: `g-ai-${stamp}-${index}`,
    title: group.title || "",
    description: group.description || "",
    order: startOrder + index,
    entries:
      group.entries?.length > 0
        ? group.entries.map((entry, entryIndex) => ({
            key: `e-ai-${stamp}-${index}-${entryIndex}`,
            description: entry.description || "",
            deadline: entry.deadline || "",
            order: entryIndex,
          }))
        : [
            {
              key: `e-ai-${stamp}-${index}-0`,
              description: "",
              deadline: "",
              order: 0,
            },
          ],
  }));
}

export function applyParsedAgenda(parsed, existingGroups = [], mode = "replace") {
  const incoming = toEditorGroups(
    parsed.groups,
    mode === "append" ? existingGroups.length : 0
  );
  return mode === "append" ? [...(existingGroups || []), ...incoming] : incoming;
}

export function serializeMeetingMinutesForExport(values = {}) {
  const payload = {
    title: values.title || "",
    description: values.description || "",
    meeting_type: values.meeting_type || "project_meeting",
    groups: (values.groups || []).map((group, index) => ({
      title: group.title || "",
      description: group.description || "",
      order: index,
      entries: (group.entries || [])
        .filter((entry) => plainText(entry.description))
        .map((entry, entryIndex) => ({
          description: entry.description || "",
          deadline: entry.deadline || "",
          order: entryIndex,
        })),
    })),
  };
  return JSON.stringify(payload, null, 2);
}

export const SAMPLE_MEETING_MINUTES_JSON = `{
  "title": "Prosjektstatus",
  "description": "Ukentlig statusmøte",
  "meeting_type": "project_meeting",
  "groups": [
    {
      "title": "Fremdrift",
      "description": "Hva er gjort siden forrige møte",
      "entries": [
        { "description": "<p>Gjennomgå milepæler og avvik mot plan.</p>" }
      ]
    },
    {
      "title": "Risiko og tiltak",
      "description": "",
      "entries": [
        { "description": "<p>Registrer nye risikoer og eier.</p>" }
      ]
    },
    {
      "title": "Neste steg",
      "entries": [
        { "description": "<p>Avtal oppfølging og frister.</p>" }
      ]
    }
  ]
}`;
